/**
 * Flight Wizard - Mobile-First Card-Based Flight Entry
 * Optimized for pilots completing flights on mobile devices
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTripById } from '@/services/tripService';
import { authFetch } from '@/services/authService';
import { API_BASE_URL, SERVER_BASE_URL } from '@/services/api';
import { startDutyPeriod, endDutyPeriod } from '@/services/dutyTimeService';
import { useAuth } from '@/contexts/AuthContext';
import { getUserFullName } from '@/types/auth';
import type { Trip } from '@/services/tripService';
import SafetyReportForm from '@/components/SafetyReportForm';
import { useActiveMelsByTailNumber } from '@/hooks/useMel';
import { FlightMelBadge } from '@/components/mel/FlightMelBadge';
import melService from '@/services/melService';

interface FlightFormData {
  tailNumber: string;
  aircraftType: string;
  fromAirport: string;
  toAirport: string;
  actualAirTime: string;
  actualFlightTime: string;
  pilotNotes: string;
  isShortNotice: boolean;
  photoFile: File | null;
  photoPreview: string | null;
}

export const FlightWizardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentFlightIndex, setCurrentFlightIndex] = useState(0);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showSafetyReport, setShowSafetyReport] = useState(false);
  const [dutyStartTime, setDutyStartTime] = useState('');
  const [dutyEndTime, setDutyEndTime] = useState('');

  // Form data for all flights
  const [flightData, setFlightData] = useState<Record<number, FlightFormData>>({});

  // Card definitions per flight
  const cards = [
    { id: 'aircraft', title: 'Aircraft', field: 'tailNumber' as keyof FlightFormData },
    { id: 'route', title: 'Route', fields: ['fromAirport', 'toAirport'] },
    { id: 'airTime', title: 'Actual Air Time', field: 'actualAirTime' as keyof FlightFormData },
    { id: 'flightTime', title: 'Actual Flight Time', field: 'actualFlightTime' as keyof FlightFormData },
    { id: 'notes', title: 'Pilot Notes', field: 'pilotNotes' as keyof FlightFormData },
    { id: 'photo', title: 'Logbook Photo', field: 'photoFile' as keyof FlightFormData },
    { id: 'shortNotice', title: 'Short Notice?', field: 'isShortNotice' as keyof FlightFormData }
  ];

  useEffect(() => {
    loadTrip();
  }, [id]);

  const loadTrip = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await getTripById(parseInt(id));
      setTrip(data);

      // Initialize form data for all flights
      const initialData: Record<number, FlightFormData> = {};
      data.flights.forEach(flight => {
        initialData[flight.id] = {
          tailNumber: flight.tailNumber || '',
          aircraftType: flight.aircraftType || '',
          fromAirport: flight.fromAirport || '',
          toAirport: flight.toAirport || '',
          actualAirTime: flight.actualAirTime?.toString() || '',
          actualFlightTime: flight.actualFlightTime?.toString() || '',
          pilotNotes: flight.pilotNotes || '',
          isShortNotice: flight.isShortNotice || false,
          photoFile: null,
          photoPreview: flight.logbookPhotoUrl || null
        };
      });
      setFlightData(initialData);
    } catch (err: any) {
      alert(err.message || 'Failed to load trip');
    } finally {
      setLoading(false);
    }
  };

  const currentFlight = trip?.flights[currentFlightIndex];
  const currentFormData = currentFlight ? flightData[currentFlight.id] : null;

  // Fetch active MELs for the current aircraft
  const currentTailNumber = currentFormData?.tailNumber?.trim().toUpperCase() || '';
  const { data: activeMels = [] } = useActiveMelsByTailNumber(currentTailNumber, currentTailNumber.length >= 4);
  const totalFlights = trip?.flights.length || 0;
  const totalCards = cards.length;
  const overallProgress = ((currentFlightIndex * totalCards + currentCardIndex) / (totalFlights * totalCards)) * 100;

  const handleNext = () => {
    if (currentCardIndex < cards.length - 1) {
      // Move to next card in current flight
      setCurrentCardIndex(currentCardIndex + 1);
    } else if (currentFlightIndex < totalFlights - 1) {
      // Move to first card of next flight
      setCurrentFlightIndex(currentFlightIndex + 1);
      setCurrentCardIndex(0);
    } else {
      // All cards complete, show review screen
      setCurrentCardIndex(cards.length); // Beyond last card = review
    }
  };

  const handleBack = () => {
    if (currentCardIndex > 0) {
      // Move to previous card in current flight
      setCurrentCardIndex(currentCardIndex - 1);
    } else if (currentFlightIndex > 0) {
      // Move to last card of previous flight
      setCurrentFlightIndex(currentFlightIndex - 1);
      setCurrentCardIndex(cards.length - 1);
    }
  };

  const updateField = (field: keyof FlightFormData, value: any) => {
    if (!currentFlight) return;

    setFlightData(prev => ({
      ...prev,
      [currentFlight.id]: {
        ...prev[currentFlight.id],
        [field]: value
      }
    }));
  };

  const handlePhotoCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentFlight) return;

    const preview = URL.createObjectURL(file);
    setFlightData(prev => ({
      ...prev,
      [currentFlight.id]: {
        ...prev[currentFlight.id],
        photoFile: file,
        photoPreview: preview
      }
    }));
  };

  const handleSubmitAll = async () => {
    if (!trip) return;

    try {
      setSubmitting(true);

      // Submit each flight
      for (const flight of trip.flights) {
        const data = flightData[flight.id];

        // Upload photo if exists
        if (data.photoFile) {
          const photoFormData = new FormData();
          photoFormData.append('photo', data.photoFile);

          await authFetch(`${API_BASE_URL}/flights/trip/${flight.id}/photo`, {
            method: 'POST',
            body: photoFormData,
            headers: {}
          });
        }

        // Update flight data
        await authFetch(`${API_BASE_URL}/flights/trip/${flight.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tailNumber: data.tailNumber,
            aircraftType: data.aircraftType,
            fromAirport: data.fromAirport,
            toAirport: data.toAirport,
            actualAirTime: parseFloat(data.actualAirTime),
            actualFlightTime: parseFloat(data.actualFlightTime),
            pilotNotes: data.pilotNotes,
            isShortNotice: data.isShortNotice,
            status: 'IN_PROGRESS'
          })
        });

        // Submit flight
        await authFetch(`${API_BASE_URL}/flights/trip/${flight.id}/submit`, {
          method: 'POST'
        });
      }

      // Record duty period if times provided
      if (dutyStartTime && dutyEndTime && trip) {
        try {
          const firstFlight = trip.flights[0];
          const lastFlight = trip.flights[trip.flights.length - 1];
          const flightDate = firstFlight?.date?.slice(0, 10) || new Date().toISOString().slice(0, 10);

          const dp = await startDutyPeriod({
            dutyStartTime: new Date(`${flightDate}T${dutyStartTime}`).toISOString(),
            startLocation: firstFlight?.fromAirport || undefined,
            tripId: trip.id,
          });

          await endDutyPeriod(dp.id, {
            dutyEndTime: new Date(`${flightDate}T${dutyEndTime}`).toISOString(),
            endLocation: lastFlight?.toAirport || undefined,
          });
        } catch {
          // Non-blocking: duty period recording is supplementary
        }
      }

      alert('All flights submitted successfully!');
      navigate('/trips');
    } catch (err: any) {
      alert(err.message || 'Failed to submit flights');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!trip || !currentFlight || !currentFormData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Trip not found</div>
      </div>
    );
  }

  // Review screen (after all cards)
  if (currentCardIndex >= cards.length) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-foreground mb-4">Review & Submit</h1>

          {trip.flights.map((flight, idx) => {
            const data = flightData[flight.id];
            return (
              <div key={flight.id} className="bg-card rounded-lg shadow border border-border p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-foreground">Flight {idx + 1}</h3>
                  <ReviewMelBadge tailNumber={data.tailNumber} />
                </div>
                <div className="text-sm text-foreground space-y-1">
                  <p>✓ Aircraft: {data.tailNumber}</p>
                  <p>✓ Route: {data.fromAirport} → {data.toAirport}</p>
                  <p>✓ Air Time: {data.actualAirTime} hrs</p>
                  <p>✓ Flight Time: {data.actualFlightTime} hrs</p>
                  {data.photoPreview && <p>✓ Photo attached</p>}
                  {data.isShortNotice && <p className="text-yellow-600 dark:text-yellow-400">⚡ Short Notice</p>}
                </div>
              </div>
            );
          })}

          {/* Duty Time Recording */}
          <div className="bg-card rounded-lg shadow border border-border p-4 mb-4">
            <h3 className="font-semibold text-foreground mb-3">Duty Period (Optional)</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Record your on-duty and off-duty times for CARs 700 compliance tracking.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Duty Start (local)</label>
                <input
                  type="time"
                  value={dutyStartTime}
                  onChange={(e) => setDutyStartTime(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Duty End (local)</label>
                <input
                  type="time"
                  value={dutyEndTime}
                  onChange={(e) => setDutyEndTime(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none text-sm"
                />
              </div>
            </div>
            {dutyStartTime && dutyEndTime && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                Duty period will be recorded on submission.
              </p>
            )}
          </div>

          <button
            onClick={handleBack}
            className="w-full mb-2 px-4 py-3 bg-muted text-foreground rounded-lg"
          >
            ← Back to Edit
          </button>
          <button
            onClick={handleSubmitAll}
            disabled={submitting}
            className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit All Flights'}
          </button>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentCardIndex];

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar */}
      <div className="bg-card shadow border-b border-border">
        <div className="h-2 bg-primary" style={{ width: `${overallProgress}%` }} />
        <div className="p-4 text-center text-sm text-muted-foreground">
          Flight {currentFlightIndex + 1} of {totalFlights} • Step {currentCardIndex + 1} of {totalCards}
        </div>
      </div>

      {/* Card */}
      <div className="max-w-md mx-auto p-4 pt-8">
        <div className="bg-card rounded-2xl shadow-lg border border-border p-6 min-h-[400px] flex flex-col">
          <h2 className="text-2xl font-bold text-foreground mb-6">{currentCard.title}</h2>

          <div className="flex-1">
            {/* Aircraft Card */}
            {currentCard.id === 'aircraft' && (
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Tail Number</label>
                <input
                  type="text"
                  value={currentFormData.tailNumber}
                  onChange={(e) => updateField('tailNumber', e.target.value)}
                  className="w-full text-3xl font-bold text-center py-4 border-2 border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none"
                  placeholder="C-FBXL"
                />
                {/* Active MEL warning */}
                {activeMels.length > 0 && (
                  <div className={`mt-4 rounded-lg border-2 p-4 ${
                    activeMels.some(m => melService.isExpired(m.expiresAt))
                      ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                      : activeMels.some(m => melService.isExpiringSoon(m.expiresAt))
                        ? 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20'
                        : 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <FlightMelBadge mels={activeMels} variant="badge" />
                      <span className="text-sm font-medium text-foreground">
                        Active on {currentFormData.tailNumber}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {activeMels.map(mel => (
                        <div key={mel.id} className="flex items-center justify-between text-xs">
                          <span className="text-foreground">
                            <span className="font-mono font-medium">{mel.melItem.itemNumber}</span>
                            {' — '}{mel.melItem.title}
                          </span>
                          <span className={`ml-2 flex-shrink-0 font-medium ${
                            melService.isExpired(mel.expiresAt)
                              ? 'text-red-700 dark:text-red-400'
                              : melService.isExpiringSoon(mel.expiresAt)
                                ? 'text-gray-900 dark:text-yellow-400'
                                : 'text-gray-700 dark:text-muted-foreground'
                          }`}>
                            Cat {mel.melItem.repairCategory} · {melService.formatExpiryStatus(mel.expiresAt)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Route Card */}
            {currentCard.id === 'route' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">From</label>
                  <input
                    type="text"
                    value={currentFormData.fromAirport}
                    onChange={(e) => updateField('fromAirport', e.target.value.toUpperCase())}
                    className="w-full text-2xl font-bold text-center py-3 border-2 border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none"
                    placeholder="CYHM"
                    maxLength={4}
                  />
                </div>
                <div className="text-center text-2xl text-muted-foreground">→</div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">To</label>
                  <input
                    type="text"
                    value={currentFormData.toAirport}
                    onChange={(e) => updateField('toAirport', e.target.value.toUpperCase())}
                    className="w-full text-2xl font-bold text-center py-3 border-2 border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none"
                    placeholder="KTEB"
                    maxLength={4}
                  />
                </div>
              </div>
            )}

            {/* Air Time Card */}
            {currentCard.id === 'airTime' && (
              <div>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={currentFormData.actualAirTime}
                  onChange={(e) => updateField('actualAirTime', e.target.value)}
                  className="w-full text-5xl font-bold text-center py-8 border-2 border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none"
                  placeholder="1.5"
                />
                <p className="text-center text-muted-foreground mt-4">hours</p>
              </div>
            )}

            {/* Flight Time Card */}
            {currentCard.id === 'flightTime' && (
              <div>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={currentFormData.actualFlightTime}
                  onChange={(e) => updateField('actualFlightTime', e.target.value)}
                  className="w-full text-5xl font-bold text-center py-8 border-2 border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none"
                  placeholder="1.6"
                />
                <p className="text-center text-muted-foreground mt-4">hours</p>
              </div>
            )}

            {/* Notes Card */}
            {currentCard.id === 'notes' && (
              <div className="space-y-4">
                <textarea
                  rows={5}
                  value={currentFormData.pilotNotes}
                  onChange={(e) => updateField('pilotNotes', e.target.value)}
                  className="w-full p-4 border-2 border-border bg-background text-foreground rounded-lg focus:border-primary focus:outline-none"
                  placeholder="Any notes about this flight..."
                />
                <button
                  type="button"
                  onClick={() => setShowSafetyReport(true)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🛡️</span>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900 dark:text-amber-200">File Safety Report</p>
                      <p className="text-xs text-gray-700 dark:text-amber-400">Report a hazard or safety event (SMS)</p>
                    </div>
                  </div>
                  <span className="text-gray-500 dark:text-amber-500">›</span>
                </button>
              </div>
            )}

            {/* Photo Card */}
            {currentCard.id === 'photo' && (
              <div className="space-y-4">
                {currentFormData.photoPreview ? (
                  <div>
                    <img
                      src={currentFormData.photoPreview.startsWith('blob:')
                        ? currentFormData.photoPreview
                        : `${SERVER_BASE_URL}${currentFormData.photoPreview}`}
                      alt="Logbook"
                      className="w-full rounded-lg"
                    />
                    <button
                      onClick={() => updateField('photoFile', null)}
                      className="w-full mt-2 py-2 text-red-600 dark:text-red-400"
                    >
                      Remove Photo
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className="block w-full py-4 bg-primary text-primary-foreground text-center rounded-lg cursor-pointer">
                      📷 Take Photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handlePhotoCapture}
                        className="hidden"
                      />
                    </label>
                    <label className="block w-full py-4 bg-muted text-foreground text-center rounded-lg cursor-pointer">
                      📁 Choose File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoCapture}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-center text-muted-foreground">Optional - can skip</p>
                  </div>
                )}
              </div>
            )}

            {/* Short Notice Card */}
            {currentCard.id === 'shortNotice' && (
              <div className="text-center py-8">
                <button
                  onClick={() => updateField('isShortNotice', !currentFormData.isShortNotice)}
                  className={`w-full py-8 text-2xl font-bold rounded-lg border-4 transition-colors ${
                    currentFormData.isShortNotice
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-500 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400'
                      : 'bg-muted border-border text-muted-foreground'
                  }`}
                >
                  {currentFormData.isShortNotice ? '⚡ YES - Short Notice' : 'NO - Regular'}
                </button>
                <p className="text-sm text-muted-foreground mt-4">
                  Was this flight booked with less than 24 hours notice?
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex gap-3 mt-6">
            {(currentFlightIndex > 0 || currentCardIndex > 0) && (
              <button
                onClick={handleBack}
                className="flex-1 py-3 bg-muted text-foreground rounded-lg font-semibold"
              >
                ← Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 py-3 bg-primary text-primary-foreground rounded-lg font-semibold"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* Safety Report Modal */}
      {showSafetyReport && currentFlight && (
        <SafetyReportForm
          flightContext={{
            date: currentFlight.date,
            tailNumber: currentFormData.tailNumber,
            aircraftType: currentFormData.aircraftType,
            fromAirport: currentFormData.fromAirport,
            toAirport: currentFormData.toAirport,
            pilotName: user ? getUserFullName(user) : 'Unknown',
            qNumber: trip.qNumber,
            tripId: trip.id,
            flightId: currentFlight.id,
          }}
          onClose={() => setShowSafetyReport(false)}
          onSubmit={async (report) => {
            const categoryMap: Record<string, string> = {
              weather: 'WEATHER', mechanical: 'MECHANICAL', atc: 'ATC',
              ground_ops: 'GROUND_OPS', human_factors: 'HUMAN_FACTORS',
              bird_strike: 'BIRD_STRIKE', runway_incursion: 'RUNWAY_INCURSION',
              cabin_safety: 'CABIN_SAFETY', other: 'OTHER',
            };
            await authFetch(`${API_BASE_URL}/safety-reports`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                anonymous: report.anonymous,
                tripId: trip.id,
                flightId: currentFlight.id,
                tailNumber: currentFormData.tailNumber,
                fromAirport: currentFormData.fromAirport,
                toAirport: currentFormData.toAirport,
                flightDate: currentFlight.date,
                narrative: report.narrative,
                hazardCategory: categoryMap[report.hazardCategory] || 'OTHER',
                severity: report.severity.toUpperCase(),
                likelihood: report.likelihood.toUpperCase(),
                actionTaken: report.actionTaken,
                recommendedFollowUp: report.recommendedFollowUp || undefined,
              }),
            });
          }}
        />
      )}
    </div>
  );
};

/**
 * Small wrapper to fetch MELs by tail number on the review screen.
 * Separate component so each flight can call the hook independently.
 */
const ReviewMelBadge: React.FC<{ tailNumber: string }> = ({ tailNumber }) => {
  const trimmed = tailNumber?.trim().toUpperCase() || '';
  const { data: mels = [] } = useActiveMelsByTailNumber(trimmed, trimmed.length >= 4);
  if (mels.length === 0) return null;
  return <FlightMelBadge mels={mels} variant="badge" />;
};
